import React from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'antd'
import { TokenStorage } from '@edulastic/api'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { isEmpty, get } from 'lodash'

import { getCalcTypeSelector } from '../../../selectors/test'
import {
  getIsAnswerMaskingEnabledSelector,
  getMaskedAnswersSelector,
  updateTestPlayerAction,
} from '../../../../author/sharedDucks/testPlayer'
import { saveUserWorkAction } from '../../../actions/userWork'
import { toolMap } from '../../../constants/assessmentPlayer'
import {
  getQuestionAndOptionsMapFromTestItemData,
  itemDataHasMultipleChoiceOptions,
} from '../../../utils/helpers'
import {
  ToolbarModalContainer,
  ToolbarButton,
} from '../../common/ToolbarModal/styled-components'

const ToolsModal = ({
  isVisible,
  onClose,
  settings = {},
  tool = [],
  changeTool,
  isDocbased = false,
  toggleUserWorkUploadModal,
  isPremiumContentWithoutAccess = false,
  checkAnswerInProgress,
  answerChecksUsedForItem,
  checkAnswer,
  calcTypes,
  i18Translate,
  openReferenceModal = () => {},
  canShowReferenceMaterial = false,
  isShowReferenceModal = false,
  isAnswerMaskingEnabled,
  updateTestPlayer,
  saveUserWork,
  crossAction,
  testItem = {},
  maskedAnswers,
}) => {
  const isCrossActionEnabled = tool.indexOf(toolMap.CROSS_OUT) !== -1

  const toolbarHandler = (value) => {
    changeTool(value)
    if (value === 3 && maskedAnswers?.[testItem?._id]) {
      updateTestPlayer({
        maskedAnswers: {
          ...maskedAnswers,
          [testItem._id]: undefined,
        },
      })
    }
    onClose()
  }

  const handleCheckAnswer = () => {
    if (checkAnswerInProgress || typeof checkAnswer !== 'function') {
      return null
    }
    checkAnswer()
    onClose()
  }

  const { _id: itemId, data: testItemData = {} } = testItem

  const {
    enableScratchpad,
    isTeacherPremium,
    enableUploadWork,
    maxAnswerChecks,
  } = settings
  const itemDataHasMultipleChoiceQuestions = itemDataHasMultipleChoiceOptions(
    testItemData
  )
  const isDisableCrossBtn =
    isAnswerMaskingEnabled || !itemDataHasMultipleChoiceQuestions
  const isDisableAnswerMakingBtn =
    isCrossActionEnabled || !itemDataHasMultipleChoiceQuestions
  const hideCheckAnswer = !TokenStorage.getAccessToken()

  const handleAnswerMasking = () => {
    const hasMaskedAnswersForCurrentItem = maskedAnswers?.[itemId]
    if (
      !isAnswerMaskingEnabled &&
      !hasMaskedAnswersForCurrentItem &&
      itemDataHasMultipleChoiceQuestions
    ) {
      updateTestPlayer({
        isAnswerMaskingEnabled: true,
        maskedAnswers: {
          ...maskedAnswers,
          [itemId]: getQuestionAndOptionsMapFromTestItemData(testItemData),
        },
      })
    } else if (!isAnswerMaskingEnabled) {
      updateTestPlayer({
        isAnswerMaskingEnabled: true,
      })
    } else {
      updateTestPlayer({
        isAnswerMaskingEnabled: false,
      })
    }
    if (crossAction) {
      saveUserWork({
        [testItem._id]: {
          crossAction: {},
        },
      })
    }
    onClose()
  }

  const handleReferenceMaterial = () => {
    openReferenceModal()
    onClose()
  }

  const handleUploadWork = () => {
    toggleUserWorkUploadModal()
    onClose()
  }

  const checkAnswerDisabled =
    isPremiumContentWithoutAccess ||
    checkAnswerInProgress ||
    answerChecksUsedForItem >= maxAnswerChecks

  return (
    <Modal
      visible={isVisible}
      onCancel={onClose}
      closable={false}
      bodyStyle={{ padding: 20 }}
      footer={null}
      centered
      width="390px"
      zIndex={1500}
    >
      <ToolbarModalContainer>
        {maxAnswerChecks > 0 && !hideCheckAnswer && (
          <ToolbarButton
            onClick={handleCheckAnswer}
            disabled={checkAnswerDisabled}
            data-cy="checkAnswer"
          >
            {checkAnswerInProgress
              ? i18Translate(
                  'student:common.test.checkAnswerInfoTexts.inProgress'
                )
              : answerChecksUsedForItem >= maxAnswerChecks
              ? i18Translate(
                  'student:common.test.checkAnswerInfoTexts.usageLimitExceeded'
                )
              : i18Translate('student:common.test.checkanswer')}
          </ToolbarButton>
        )}
        {!isEmpty(calcTypes) && (
          <ToolbarButton
            active={tool.indexOf(2) !== -1}
            disabled={isPremiumContentWithoutAccess}
            onClick={() => toolbarHandler(2)}
          >
            {i18Translate('header:toolbar.calculator')}
          </ToolbarButton>
        )}
        {!isDocbased && canShowReferenceMaterial && (
          <ToolbarButton
            onClick={handleReferenceMaterial}
            active={isShowReferenceModal}
          >
            {i18Translate('header:toolbar.refMaterial')}
          </ToolbarButton>
        )}
        {!isDocbased && (
          <>
            <ToolbarButton
              active={tool.indexOf(toolMap.CROSS_OUT) !== -1}
              disabled={isDisableCrossBtn || isPremiumContentWithoutAccess}
              onClick={() => toolbarHandler(3)}
            >
              {i18Translate('header:toolbar.cross')}
            </ToolbarButton>
            <ToolbarButton
              active={isAnswerMaskingEnabled}
              disabled={
                isDisableAnswerMakingBtn || isPremiumContentWithoutAccess
              }
              onClick={handleAnswerMasking}
            >
              {i18Translate('header:toolbar.answerMasking')}
            </ToolbarButton>
          </>
        )}
        {!isDocbased && enableScratchpad && (
          <ToolbarButton
            active={tool.indexOf(5) !== -1}
            disabled={isPremiumContentWithoutAccess}
            onClick={() => toolbarHandler(5)}
          >
            {i18Translate('header:toolbar.scratchPad')}
          </ToolbarButton>
        )}
        {!isDocbased && isTeacherPremium && enableUploadWork && (
          <ToolbarButton
            disabled={isPremiumContentWithoutAccess}
            onClick={handleUploadWork}
          >
            {i18Translate('header:toolbar.uploadWork')}
          </ToolbarButton>
        )}
      </ToolbarModalContainer>
    </Modal>
  )
}

ToolsModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  changeTool: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
  tool: PropTypes.array.isRequired,
  openReferenceModal: PropTypes.func.isRequired,
  canShowReferenceMaterial: PropTypes.bool.isRequired,
  isShowReferenceModal: PropTypes.bool.isRequired,
  i18Translate: PropTypes.func.isRequired,
}

const enhance = compose(
  connect(
    (state, { itemId }) => ({
      checkAnswerInProgress: state?.test?.checkAnswerInProgress,
      calcTypes: getCalcTypeSelector(state),
      isAnswerMaskingEnabled: getIsAnswerMaskingEnabledSelector(state),
      crossAction: get(state, `userWork.present[${itemId}].crossAction`, null),
      maskedAnswers: getMaskedAnswersSelector(state),
    }),
    {
      updateTestPlayer: updateTestPlayerAction,
      saveUserWork: saveUserWorkAction,
    }
  )
)

export default enhance(ToolsModal)
